import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = {
  nome: "", cognome: "", data_nascita: "", telefono: "", email: "", note: "",
  consenso_marketing: false, consenso_whatsapp: false, consenso_email: false,
  data_consenso: "", modalita_raccolta: "",
  data_ultimo_controllo: "", data_ultimo_acquisto: "",
};

export default function ClienteForm({ open, onOpenChange, onSaved, cliente }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!cliente;

  useEffect(() => {
    if (open) setForm(cliente ? { ...EMPTY, ...cliente } : EMPTY);
  }, [open, cliente]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (isEdit) await api.put(`/clienti/${cliente.id}`, payload);
      else await api.post("/clienti", payload);
      toast.success(isEdit ? "Cliente aggiornato" : "Cliente creato");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto custom-scroll" data-testid="cliente-form">
        <DialogHeader>
          <DialogTitle className="font-head">{isEdit ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Anagrafica</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input data-testid="cliente-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Cognome *</Label>
                <Input data-testid="cliente-cognome" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Data di nascita</Label>
                <Input type="date" data-testid="cliente-data-nascita" value={form.data_nascita || ""} onChange={(e) => set("data_nascita", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Telefono</Label>
                <Input data-testid="cliente-telefono" value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Email</Label>
                <Input type="email" data-testid="cliente-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Note</Label>
                <Textarea data-testid="cliente-note" value={form.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Consensi GDPR</div>
            <div className="space-y-3">
              {[
                ["consenso_marketing", "Consenso marketing"],
                ["consenso_whatsapp", "Consenso WhatsApp"],
                ["consenso_email", "Consenso email"],
              ].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{label}</span>
                  <Switch data-testid={`switch-${k}`} checked={!!form[k]} onCheckedChange={(v) => set(k, v)} />
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="space-y-1.5">
                  <Label>Data consenso</Label>
                  <Input type="date" data-testid="cliente-data-consenso" value={form.data_consenso || ""} onChange={(e) => set("data_consenso", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Modalità di raccolta</Label>
                  <Select value={form.modalita_raccolta || ""} onValueChange={(v) => set("modalita_raccolta", v)}>
                    <SelectTrigger data-testid="cliente-modalita-raccolta"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cartaceo">Cartaceo</SelectItem>
                      <SelectItem value="digitale">Digitale</SelectItem>
                      <SelectItem value="verbale">Verbale registrato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Date richiamo (marketing-safe)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Data ultimo controllo</Label>
                <Input type="date" data-testid="cliente-ultimo-controllo" value={form.data_ultimo_controllo || ""} onChange={(e) => set("data_ultimo_controllo", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Data ultimo acquisto</Label>
                <Input type="date" data-testid="cliente-ultimo-acquisto" value={form.data_ultimo_acquisto || ""} onChange={(e) => set("data_ultimo_acquisto", e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="save-cliente-btn" onClick={submit} disabled={saving} className="bg-[#1E40AF] hover:bg-[#1D4ED8]">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
